import { useCallback, useEffect, useMemo, useState } from 'react';
import isEqual from 'lodash.isequal';
import { Checkbox, Flex, Stack, createStyles, useMantineTheme } from '@mantine/core';
import { IconChevronDown, IconLock } from '@tabler/icons-react';

import { Button } from '~/domains/design-system/Button';
import { Title } from '~/domains/design-system/Title';
import { Text } from '~/domains/design-system/Text';
import { Tooltip } from '~/domains/common/components/Tooltip/Tooltip';
import { Drawer } from '~/domains/design-system/components/Drawer/Drawer';
import { TruncateText } from '~/domains/design-system/TruncateText';

import type { StaticFieldProps } from '../../types/common';
import { getFilteredSelectOptions } from '../utils/getFilteredSelectOptions';
import { getDependenciesValues } from '../../../context/utils/getDependencyValues';
import { getIsDependenciesSatisfied } from '../../../context/utils/getIsDependenciesSatisfied';

import type { MultiSelectModalField } from './types';
import { getNewSelectValuesOnOptionsChange } from './getNewSelectValuesOnOptionsChange';

const useStyles = createStyles((theme) => ({
  card: {
    minHeight: 48,
    width: '100%',
    paddingLeft: theme.other.spacing._12,
    paddingRight: theme.other.spacing._12,
    paddingTop: theme.other.spacing._8,
    paddingBottom: theme.other.spacing._8,
    borderRadius: theme.radius.sm,
    border: `1px solid ${theme.colors.gray[3]}`,
    textAlign: 'left',
  },
  error: {
    borderColor: theme.colors.red[6],
  },
}));

function SelectModalTitle({ label }: { label?: string }) {
  return label ? (
    <Title ta="center" variant="headline-large">
      {label}
    </Title>
  ) : null;
}

export function MultiSelectModalFieldElement({
  field,
  value,
  onValueChange,
  errors,
  isTouched,
  allValues,
  allErrors,
  sectionKey,
}: StaticFieldProps<MultiSelectModalField>) {
  const [isOpen, setIsOpen] = useState(false);

  const { cx, classes } = useStyles();
  const theme = useMantineTheme();
  const options = useMemo(
    () =>
      getFilteredSelectOptions({
        options: field.options,
        sectionKey,
        allValues,
        allErrors,
      }),
    [allErrors, allValues, field.options, sectionKey]
  );

  useEffect(() => {
    if (field.validation?.isOptionalWhen) {
      const dependencyValues = getDependenciesValues(
        allValues,
        sectionKey,
        field.validation?.isOptionalWhen
      );
      const isDependencySatisfied = getIsDependenciesSatisfied(
        sectionKey,
        field.validation?.isOptionalWhen,
        allErrors,
        dependencyValues
      );

      onValueChange((prev) => {
        if (prev?.length === 0 || !prev) {
          return isDependencySatisfied ? [] : undefined;
        }
        return prev;
      }, true);
    }
  }, [field.validation?.isOptionalWhen, sectionKey, allErrors]);

  const safeValue = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const includesSelectAll = useMemo(() => options.some((o) => o.selectAll), [options]);
  const isAllSelected = useMemo(
    () => safeValue.length === options.filter((o) => !o.selectAll).length,
    [options, safeValue.length]
  );
  const optionsValue = useMemo(() => {
    if (isAllSelected) {
      return ['all'];
    }
    return safeValue;
  }, [isAllSelected, safeValue]);

  const selectedValueSet = useMemo(
    () => new Set((Array.isArray(optionsValue) ? optionsValue : []).map((v) => String(v))),
    [optionsValue]
  );
  const labelFromOptions = useMemo(() => {
    if (isAllSelected) {
      return field.options
        .filter((o) => !o.selectAll)
        .map((o) => o.label)
        .join(', ');
    }
    if (selectedValueSet.size > 0) {
      return field.options
        .filter((o) => selectedValueSet.has(String(o.value)))
        .map((o) => o.label)
        .join(', ');
    }
    return '';
  }, [isAllSelected, selectedValueSet, field.options]);

  useEffect(() => {
    const newValues = getNewSelectValuesOnOptionsChange({
      options,
      value: safeValue,
    });

    if (isEqual(newValues, safeValue)) {
      return;
    }
    if (safeValue.length > 0 && newValues.length === 0) {
      return;
    }

    return onValueChange(newValues);
  }, [options, includesSelectAll, onValueChange, safeValue]);

  const handleOnChange = useCallback(
    (e) => {
      const { target } = e;
      const optionValue = target.value;
      const allOptionsValues = options.map((option) => option.value).filter((v) => v !== 'all');

      onValueChange((prev = []) => {
        if (!Array.isArray(prev)) {
          return [optionValue];
        }
        if (optionValue === 'all') {
          return isAllSelected ? [] : allOptionsValues;
        }
        if (prev.includes(optionValue)) {
          return prev.filter((v) => v !== optionValue);
        }

        const newValue = [...prev, optionValue];

        return newValue;
      });
    },
    [options, onValueChange, isAllSelected]
  );

  if (options.length === 0) {
    return null;
  }

  const displayText =
    Array.isArray(optionsValue) && optionsValue.length > 0 ? labelFromOptions : '';

  return (
    <div>
      <Button
        variant="text"
        disabled={field?.disabled}
        onClick={() => setIsOpen(true)}
        className={cx(classes.card, { [classes.error]: isTouched && errors })}
      >
        <Flex align="center" justify="space-between">
          <Stack spacing={0} w="100%">
            <Text variant="body-small" c={theme.colors.gray[6]}>
              {field.label}
            </Text>
            {displayText ? (
              <TruncateText
                // defensive states for tooltip to re-render when drawer is opened/closed
                key={isOpen ? 'drawer-open' : 'drawer-closed'}
                tooltipLabel={labelFromOptions}
                variant="title-medium"
                // disable hover/touch states when drawer is open so tooltip doesn't overlap drawer when clicking the field on mobile. (EDGE-25979)
                events={isOpen ? { hover: false, focus: false, touch: false } : undefined}
              >
                {labelFromOptions}
              </TruncateText>
            ) : (
              <Text variant="title-medium">None </Text>
            )}
          </Stack>

          {field?.locked ? (
            <IconLock size={24} stroke={theme.colors.gray[4]} strokeWidth={1.5} />
          ) : (
            <IconChevronDown size={18} stroke={theme.colors.gray[4]} />
          )}
        </Flex>
      </Button>
      {isTouched && errors && (
        <Text variant="label-small" color="orange.3">
          {errors.join(', ')}
        </Text>
      )}
      {field?.subLabel && (
        <Text component="div" mt={theme.other.spacing._8} c="gray2.3" variant="body-small">
          {field.subLabel}
        </Text>
      )}
      <Drawer
        title={<SelectModalTitle label={field?.label} />}
        onClose={() => setIsOpen(false)}
        onToggle={setIsOpen}
        open={isOpen}
      >
        <Stack>
          {options?.map((option, i) => {
            const isChecked = Array.isArray(value)
              ? value.includes(option.value as string | number) || isAllSelected
              : value === option.value;

            return (
              <Tooltip
                isRendered={!!option.params?.tooltip}
                withinPortal
                position="top-start"
                key={`${option.value}`}
                label={option.params?.tooltip}
              >
                <span>
                  <Checkbox
                    pb={theme.other.spacing._8}
                    radius="lg"
                    checked={isChecked}
                    classNames={classes}
                    disabled={option.isDisabled}
                    sx={{ borderBottom: `1px solid ${theme.colors.gray[1]}` }}
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    onChange={() => handleOnChange({ target: { value: option.value } })}
                    label={<Text variant="headline-medium">{option.label}</Text>}
                  />
                </span>
              </Tooltip>
            );
          })}
        </Stack>
      </Drawer>
    </div>
  );
}
